import * as React from 'react'

import type { CtaViewModel } from '../../view-models/CtaViewModel'
import { ButtonTypes } from '@/view-models/ButtonTypes'
import { Button, LinkButton } from '@/components/Button/Button'

type Props = CtaViewModel & {
  onClick?: (...args: Array<any>) => any
  children?: React.ReactNode
  className?: string
  eventMeta?: {
    event: string
    eventData?: string
  }
  small?: boolean
  /**
   * The type the button should render as.
   * It is ignored if the button renders link.
   */
  type?: 'button' | 'submit' | 'reset' | undefined
  /**
   * Use this when the button is rendered on a dark background for accessibility
   */
  darkBg?: boolean
  /** @deprecated Not supported, use custom styles for edge cases */
  flat?: boolean
  disabled?: boolean
  /** @deprecated no underline is default style */
  noUnderline?: boolean
  /** @deprecated Automatically renders as button if no link is provided */
  noLink?: boolean
}

function CtaButton({
  className,
  noLink,
  icon,
  link,
  flat,
  version,
  withLinkArrow = true,
  noUnderline,
  iconRight,
  iconLeft,
  ...rest
}: Props) {
  // Determine the button style based on version
  let buttonVariant: 'primary' | 'secondary' = 'primary'
  let isLinkStyle = false

  if (version !== undefined) {
    if (version === ButtonTypes.link || version === 0) {
      isLinkStyle = true
      buttonVariant = 'primary' // Default for regular button if we can't use link style
    } else if (version === ButtonTypes.secondary || version === 2) {
      buttonVariant = 'secondary'
    } else if (version === ButtonTypes.primary || version === 1) {
      buttonVariant = 'primary'
    }
  } else if (flat) {
    // For backward compatibility
    buttonVariant = 'secondary'
  }

  // If link is provided, render a LinkButton
  if (link?.href) {
    // LinkButton accepts 'link' as a variant
    const linkVariant: 'primary' | 'secondary' | 'link' = isLinkStyle
      ? 'link'
      : buttonVariant

    return (
      <LinkButton
        className={className}
        variant={linkVariant}
        {...link}
        withArrow={withLinkArrow}
        darkBg={rest.darkBg}
        disabled={rest.disabled}
        small={rest.small}
        onClick={rest.onClick}
        iconRight={iconRight}
        iconLeft={iconLeft}
        {...(rest.eventMeta && { eventMeta: rest.eventMeta })}
      >
        {rest.children || link.label}
      </LinkButton>
    )
  }

  // For regular Button, only pass valid variant values
  return (
    <Button
      className={className}
      iconLeft={iconLeft || icon}
      iconRight={iconRight}
      variant={buttonVariant} // Only 'primary' or 'secondary' for regular Button
      type={rest.type}
      darkBg={rest.darkBg}
      disabled={rest.disabled}
      small={rest.small}
      onClick={rest.onClick}
      {...(rest.eventMeta && { eventMeta: rest.eventMeta })}
    >
      {rest.children || link?.label}
    </Button>
  )
}

CtaButton.displayName = 'CtaButton'
CtaButton.defaultProps = {}
export default CtaButton
